import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, CheckCircle, Clock, Trash2, Receipt, FileText, Download, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImplementationPublicUpload = () => {
  const { shareId } = useParams();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [project, setProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [generalDocuments, setGeneralDocuments] = useState({});

  const hasFile = (doc) => doc.file_path || (doc.file_paths && doc.file_paths.length > 0);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Extract display filename from a file path string
  const extractFilename = (fp) => {
    if (!fp) return 'Αρχείο';
    if (fp.startsWith('r2://')) {
      const parts = fp.substring(5).split('/');
      const raw = parts[parts.length - 1];
      const match = raw.match(/^[a-f0-9-]{36}_(.+)$/i);
      return match ? decodeURIComponent(match[1]) : decodeURIComponent(raw);
    }
    if (fp.startsWith('gridfs:')) {
      const parts = fp.split(':');
      return parts.length >= 3 ? parts.slice(2).join(':') : 'Αρχείο';
    }
    const parts = fp.split('/');
    return parts[parts.length - 1];
  };

  const handleDeleteSingleFile = async (documentId, fileIndex, isGeneral = false) => {
    try {
      const endpoint = isGeneral
        ? `${API}/implementation/general-documents/${documentId}/file?file_index=${fileIndex}`
        : `${API}/implementation/documents/${documentId}/file?file_index=${fileIndex}`;
      await axios.delete(endpoint);
      toast.success('Το αρχείο διαγράφηκε');
      await fetchSupplierDocuments(verifiedEmail);
    } catch (error) {
      toast.error('Σφάλμα διαγραφής αρχείου');
    }
  };

  useEffect(() => {
    fetchProjectInfo();
  }, [shareId]);

  const fetchProjectInfo = async () => {
    try {
      const response = await axios.get(`${API}/implementation/public/project/${shareId}`);
      setProject(response.data.project);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης έργου');
    }
  };

  const fetchSupplierDocuments = async (supplierEmail) => {
    try {
      const response = await axios.get(`${API}/implementation/public/project/${shareId}/documents/${encodeURIComponent(supplierEmail)}`);
      setInvoices(response.data.invoices || []);
      setGeneralDocuments(response.data.general_documents || {});
    } catch (error) {
      console.error('Error fetching documents:', error);
      setInvoices([]);
      setGeneralDocuments({});
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (email !== emailConfirm) {
      toast.error('Τα emails δεν ταιριάζουν');
      return;
    }

    try {
      await axios.post(`${API}/implementation/public/project/${shareId}/email`, {
        email,
        email_confirm: emailConfirm,
      });
      toast.success('Το email επιβεβαιώθηκε');
      setVerifiedEmail(email);
      await fetchSupplierDocuments(email);
      setStep('upload');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα επιβεβαίωσης email');
    }
  };

  const handleUpload = async (documentId, files, isGeneral = false) => {
    if (!files || files.length === 0) return;

    setUploadingDocId(documentId);
    const formData = new FormData();
    
    // Support multiple files
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      // Use public endpoint for collaborators
      const endpoint = `${API}/implementation/public/documents/${documentId}/upload`;
      await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${files.length} αρχείο(α) ανέβηκαν επιτυχώς`);
      await fetchSupplierDocuments(verifiedEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ανεβάσματος αρχείου');
    } finally {
      setUploadingDocId(null);
    }
  };

  const handleDeleteFile = async (documentId, isGeneral = false) => {
    try {
      const endpoint = isGeneral
        ? `${API}/implementation/general-documents/${documentId}/file?file_index=-1`
        : `${API}/implementation/documents/${documentId}/file?file_index=-1`;
      await axios.delete(endpoint);
      toast.success('Όλα τα αρχεία διαγράφηκαν');
      await fetchSupplierDocuments(verifiedEmail);
    } catch (error) {
      toast.error('Σφάλμα διαγραφής αρχείων');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/implementation/public/project/${shareId}/submit`, null, {
        params: { supplier_email: verifiedEmail }
      });
      toast.success('Η υποβολή ολοκληρώθηκε επιτυχώς!');
    } catch (error) {
      toast.error('Σφάλμα υποβολής');
    } finally {
      setSubmitting(false);
    }
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Φόρτωση...</p>
        </div>
      </div>
    );
  }

  if (step === 'email') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                {project.project_title}
              </h1>
              <p className="text-slate-600">Πελάτης: {project.client_name}</p>
              <p className="text-sm text-blue-600 mt-1">Υλοποίηση Επενδυτικού Σχεδίου</p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Παρακαλώ εισάγετε το email σας</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-confirm">Επιβεβαίωση Email</Label>
                <Input
                  id="email-confirm"
                  type="email"
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                  required
                />
              </div>

              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800">
                Συνέχεια →
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">
            {project.project_title}
          </h1>
          <p className="text-slate-600">Πελάτης: {project.client_name}</p>
          <p className="text-sm text-blue-600">Υλοποίηση Επενδυτικού Σχεδίου</p>
          {verifiedEmail && (
            <p className="text-sm text-slate-500 mt-1">Συνδεδεμένος ως: {verifiedEmail}</p>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6">
            Τα Δικαιολογητικά σας
          </h2>

          {Object.keys(generalDocuments).length === 0 && invoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>Δεν σας έχουν ανατεθεί δικαιολογητικά ακόμα.</p>
              <p className="text-sm mt-2">Επικοινωνήστε με τον σύμβουλό σας.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* General Documents Section */}
              {Object.keys(generalDocuments).length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-emerald-600" />
                    Γενικά Δικαιολογητικά
                  </h3>
                  {Object.entries(generalDocuments).map(([category, docs]) => (
                    <div key={category} className="border border-slate-200 rounded-md">
                      <div className="px-4 py-3 bg-emerald-50 border-b border-slate-200">
                        <span className="font-medium text-emerald-800">{category}</span>
                      </div>
                      <div className="p-4 space-y-3">
                        {docs.map((doc) => (
                          <div
                            key={doc.document_id}
                            className={`border rounded-md p-4 transition-colors ${
                              doc.review_status === 'rejected' 
                                ? 'border-rose-300 bg-rose-50' 
                                : 'border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h3 className={`font-medium mb-1 ${doc.review_status === 'rejected' ? 'text-rose-900' : 'text-slate-900'}`}>
                                  {doc.document_name}
                                </h3>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {doc.review_status === 'rejected' ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-rose-100 text-rose-700 text-xs font-medium">
                                      ❌ Λάθος - Απαιτείται διόρθωση
                                    </span>
                                  ) : doc.review_status === 'approved' ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Εγκρίθηκε
                                    </span>
                                  ) : hasFile(doc) ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Ανέβηκε - Αναμονή ελέγχου
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium">
                                      <Clock className="h-3 w-3 mr-1" />
                                      Εκκρεμεί
                                    </span>
                                  )}
                                </div>
                                {doc.review_status === 'rejected' && doc.review_comment && (
                                  <div className="mt-2 p-2 bg-rose-100 rounded-md">
                                    <p className="text-sm text-rose-800">
                                      <strong>Πρόβλημα:</strong> {doc.review_comment}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Uploaded files list */}
                                {hasFile(doc) && doc.file_paths && doc.file_paths.length > 0 && (
                                  <div className="mt-3 space-y-1">
                                    <p className="text-xs font-medium text-slate-500 mb-1">Ανεβασμένα αρχεία ({doc.file_paths.length}):</p>
                                    {doc.file_paths.map((fp, idx) => (
                                      <div key={idx} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1">
                                        <span className="flex-1 truncate text-slate-700" title={extractFilename(fp)}>{extractFilename(fp)}</span>
                                        <button
                                          onClick={() => window.open(`${API}/implementation/general-documents/${doc.document_id}/download?file_index=${idx}`, '_blank')}
                                          className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                                          title="Λήψη"
                                        >
                                          <Download className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteSingleFile(doc.document_id, idx, true)}
                                          className="text-rose-500 hover:text-rose-700 flex-shrink-0"
                                          title="Διαγραφή"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 ml-4">
                                <input
                                  type="file"
                                  id={`gen-file-${doc.document_id}`}
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => handleUpload(doc.document_id, e.target.files, true)}
                                />
                                {doc.review_status === 'rejected' ? (
                                  <Button
                                    onClick={() => document.getElementById(`gen-file-${doc.document_id}`).click()}
                                    disabled={uploadingDocId === doc.document_id}
                                    className="bg-rose-600 hover:bg-rose-700"
                                  >
                                    <Upload className="h-4 w-4 mr-2" />
                                    {uploadingDocId === doc.document_id ? 'Ανέβασμα...' : 'Διόρθωση'}
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={() => document.getElementById(`gen-file-${doc.document_id}`).click()}
                                    disabled={uploadingDocId === doc.document_id}
                                    className={hasFile(doc) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"}
                                  >
                                    {hasFile(doc) ? <Plus className="h-4 w-4 mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                                    {uploadingDocId === doc.document_id 
                                      ? 'Ανέβασμα...' 
                                      : hasFile(doc)
                                        ? 'Προσθήκη επιπλέον εγγράφων'
                                        : 'Προσθήκη εγγράφου'}
                                  </Button>
                                )}
                                {hasFile(doc) && (
                                  <Button
                                    onClick={() => handleDeleteFile(doc.document_id, true)}
                                    variant="outline"
                                    size="sm"
                                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Διαγραφή όλων
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Invoice Documents Section */}
              {invoices.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-blue-600" />
                    Δικαιολογητικά Τιμολογίων
                  </h3>
              {invoices.map((invoice) => (
                <div key={invoice.invoice_id} className="border border-slate-200 rounded-md">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">Τιμολόγιο: {invoice.invoice_number}</span>
                  </div>
                  
                  <div className="p-4 space-y-3">
                    {invoice.documents.map((doc) => (
                      <div
                        key={doc.document_id}
                        className={`border rounded-md p-4 transition-colors ${
                          doc.review_status === 'rejected' 
                            ? 'border-rose-300 bg-rose-50' 
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className={`font-medium mb-1 ${doc.review_status === 'rejected' ? 'text-rose-900' : 'text-slate-900'}`}>
                              {doc.document_name}
                            </h3>
                            
                            <div className="flex items-center gap-2 flex-wrap">
                              {doc.review_status === 'rejected' ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-md bg-rose-100 text-rose-700 text-xs font-medium">
                                  ❌ Λάθος - Απαιτείται διόρθωση
                                </span>
                              ) : doc.review_status === 'approved' ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Εγκρίθηκε
                                </span>
                              ) : hasFile(doc) ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Ανέβηκε - Αναμονή ελέγχου
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Εκκρεμεί
                                </span>
                              )}
                            </div>
                            
                            {doc.review_status === 'rejected' && doc.review_comment && (
                              <div className="mt-2 p-2 bg-rose-100 rounded-md">
                                <p className="text-sm text-rose-800">
                                  <strong>Πρόβλημα:</strong> {doc.review_comment}
                                </p>
                              </div>
                            )}
                            
                            {/* Uploaded files list */}
                            {hasFile(doc) && doc.file_paths && doc.file_paths.length > 0 && (
                              <div className="mt-3 space-y-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">Ανεβασμένα αρχεία ({doc.file_paths.length}):</p>
                                {doc.file_paths.map((fp, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1">
                                    <span className="flex-1 truncate text-slate-700" title={extractFilename(fp)}>{extractFilename(fp)}</span>
                                    <button
                                      onClick={() => window.open(`${API}/implementation/documents/${doc.document_id}/download?file_index=${idx}`, '_blank')}
                                      className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                                      title="Λήψη"
                                    >
                                      <Download className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteSingleFile(doc.document_id, idx, false)}
                                      className="text-rose-500 hover:text-rose-700 flex-shrink-0"
                                      title="Διαγραφή"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2 ml-4">
                            <input
                              type="file"
                              id={`impl-public-file-${doc.document_id}`}
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                              multiple
                              className="hidden"
                              onChange={(e) => handleUpload(doc.document_id, e.target.files, false)}
                            />
                            {doc.review_status === 'rejected' ? (
                              <Button
                                onClick={() => document.getElementById(`impl-public-file-${doc.document_id}`).click()}
                                disabled={uploadingDocId === doc.document_id}
                                className="bg-rose-600 hover:bg-rose-700"
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                {uploadingDocId === doc.document_id ? 'Ανέβασμα...' : 'Διόρθωση'}
                              </Button>
                            ) : (
                              <Button
                                onClick={() => document.getElementById(`impl-public-file-${doc.document_id}`).click()}
                                disabled={uploadingDocId === doc.document_id}
                                className={hasFile(doc) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"}
                              >
                                {hasFile(doc) ? <Plus className="h-4 w-4 mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                                {uploadingDocId === doc.document_id 
                                  ? 'Ανέβασμα...' 
                                  : hasFile(doc)
                                    ? 'Προσθήκη επιπλέον εγγράφων'
                                    : 'Προσθήκη εγγράφου'}
                              </Button>
                            )}
                            {hasFile(doc) && (
                              <Button
                                onClick={() => handleDeleteFile(doc.document_id, false)}
                                variant="outline"
                                size="sm"
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Διαγραφή όλων
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-6">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
            >
              {submitting ? 'Υποβολή...' : 'Υποβολή'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImplementationPublicUpload;
